/*!
 * JEJU AI Skincare — Selfie-based dermatology assistant
 * --------------------------------------------------------------
 * Flow:
 *   1. User opens the dialog → "Take Selfie"
 *   2. Camera (MediaPipe FaceMesh) captures a frame + facial landmarks
 *   3. Analyzer computes 6 dermatologic metrics from pixel statistics
 *      inside the face oval: dark-spots, redness, under-eye darkness,
 *      texture (smoothness inverse), oiliness, dryness
 *   4. Recommendation engine maps metrics → JEJU catalog products with
 *      a morning / evening routine and dosage (light / moderate / heavy)
 *   5. Dermatologist tips + food recommendations are personalised by
 *      the top concerns
 *   6. A 30-day "after" preview is generated by running the AR effect
 *      stack at Week-4 intensity on the captured frame
 *   7. User can download the full report (PNG) or email a copy
 *
 * No external API calls — all analysis runs in-browser.
 */
(function () {
  'use strict';
  if (window.__JEJU_AI__) return;
  window.__JEJU_AI__ = true;

  const MEDIAPIPE_FACEMESH_URL = 'https://cdn.jsdelivr.net/npm/@mediapipe/face_mesh/face_mesh.js';

  // ── JEJU catalog (skincare-relevant SKUs) ───────────────────────────
  const CATALOG = {
    spot:        { handle: 'jeju-spot-fading-serum',          title: 'Jeju Spot Fading Serum',         use: 'AM + PM, after cleansing, before moisturiser', dose: '2-3 drops on affected areas' },
    eye:         { handle: 'eye-cream',                       title: 'JEJU Eye Cream',                 use: 'AM + PM, tap gently with ring finger',          dose: 'Rice-grain size per eye' },
    regenerate:  { handle: 'jeju-regenerate-cream',           title: 'JEJU Regenerate Cream',          use: 'PM only (night repair)',                        dose: 'Pea-size, full face & neck' },
    ageReversal: { handle: 'jeju-age-reversal-cream',         title: 'JEJU Age-Reversal Cream',        use: 'AM + PM, last skincare step before SPF',        dose: 'Almond-size, face & neck' },
    set:         { handle: 'jeju-essential-skincare-set',     title: 'JEJU Essential Skincare Set',    use: 'Full routine AM + PM',                          dose: 'As per individual product steps' },
    sunscreen:   { handle: 'jeju-multi-defense-sunscreen-50-pa', title: 'JEJU Multi-Defense Sunscreen SPF 50 PA+++', use: 'AM only, last step, re-apply every 2 h outdoors', dose: '2 finger-lengths for face & neck' },
    lip:         { handle: 'vegan-lips-moisturizer',          title: 'JEJU Vegan Lip Care',            use: 'AM + PM and as needed',                         dose: 'Thin layer on lips' },
    ipl:         { handle: 'ipl-skin-skin-device-with-7-colors', title: 'JEJU IPL Skin Device (7-Color)', use: '2-3× per week, 10 min per session',          dose: 'Red light for collagen, blue for blemishes' }
  };

  // ── State ───────────────────────────────────────────────────────────
  let dialog = null;
  let videoEl = null;
  let captureCanvas = null;
  let stream = null;
  let faceMesh = null;
  let lastLandmarks = null;
  let lastFrame = null; // ImageData of captured selfie
  let lastFrameW = 0, lastFrameH = 0;
  let lastReport = null; // { metrics, recommendations, tips, foods, beforeDataUrl, afterDataUrl }

  // ── Boot ────────────────────────────────────────────────────────────
  document.addEventListener('DOMContentLoaded', init);
  if (document.readyState !== 'loading') init();

  function init() {
    dialog = document.querySelector('[data-jeju-ai-dialog]');
    if (!dialog || dialog.dataset.aiInit) return;
    dialog.dataset.aiInit = '1';

    videoEl       = dialog.querySelector('[data-jeju-ai-video]');
    captureCanvas = dialog.querySelector('[data-jeju-ai-canvas]');

    // Wire launchers
    document.querySelectorAll('[data-jeju-ai-open]').forEach(btn => {
      btn.addEventListener('click', openDialog);
    });

    dialog.querySelectorAll('[data-jeju-ai-close]').forEach(btn => {
      btn.addEventListener('click', closeDialog);
    });
    dialog.querySelector('[data-jeju-ai-start]')?.addEventListener('click', startCamera);
    dialog.querySelector('[data-jeju-ai-capture]')?.addEventListener('click', captureAndAnalyze);
    dialog.querySelector('[data-jeju-ai-retake]')?.addEventListener('click', resetToCamera);
    dialog.querySelector('[data-jeju-ai-download]')?.addEventListener('click', downloadReport);
    dialog.querySelector('[data-jeju-ai-email-form]')?.addEventListener('submit', submitEmail);
  }

  function openDialog() {
    if (!dialog) return;
    showPanel('intro');
    if (typeof dialog.showModal === 'function') dialog.showModal();
    else dialog.setAttribute('open', '');
  }
  function closeDialog() {
    stopCamera();
    if (typeof dialog.close === 'function') dialog.close();
    else dialog.removeAttribute('open');
  }
  function showPanel(name) {
    dialog.querySelectorAll('[data-jeju-ai-panel]').forEach(p => {
      p.hidden = p.dataset.jejuAiPanel !== name;
    });
  }

  // ── Camera lifecycle ────────────────────────────────────────────────
  async function startCamera() {
    try {
      showPanel('camera');
      setStatus('Requesting camera permission…');
      if (!navigator.mediaDevices?.getUserMedia) throw new Error('Camera API not available');
      stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'user', width: { ideal: 1280 }, height: { ideal: 720 } },
        audio: false
      });
      videoEl.srcObject = stream;
      await videoEl.play();
      setStatus('Centre your face in the frame, then tap "Scan my skin".');
      await ensureFaceMesh();
    } catch (e) {
      console.error('[JEJU AI] camera error', e);
      setStatus('Could not start camera: ' + (e.message || e), true);
    }
  }
  function stopCamera() {
    if (stream) { stream.getTracks().forEach(t => t.stop()); stream = null; }
    if (videoEl) videoEl.srcObject = null;
  }
  function setStatus(msg, isError) {
    const el = dialog.querySelector('[data-jeju-ai-status]');
    if (!el) return;
    el.textContent = msg || '';
    el.dataset.error = isError ? '1' : '';
  }
  function resetToCamera() {
    lastFrame = null;
    lastLandmarks = null;
    lastReport = null;
    startCamera();
  }

  // ── MediaPipe FaceMesh ──────────────────────────────────────────────
  function loadScript(url) {
    return new Promise((resolve, reject) => {
      if (document.querySelector(`script[src="${url}"]`)) return resolve();
      const s = document.createElement('script');
      s.src = url;
      s.crossOrigin = 'anonymous';
      s.onload = resolve;
      s.onerror = () => reject(new Error('Failed to load ' + url));
      document.head.appendChild(s);
    });
  }
  async function ensureFaceMesh() {
    if (faceMesh) return faceMesh;
    await loadScript(MEDIAPIPE_FACEMESH_URL);
    if (!window.FaceMesh) throw new Error('FaceMesh did not load');
    faceMesh = new window.FaceMesh({
      locateFile: (file) => `https://cdn.jsdelivr.net/npm/@mediapipe/face_mesh/${file}`
    });
    faceMesh.setOptions({ maxNumFaces: 1, refineLandmarks: true, minDetectionConfidence: 0.5, minTrackingConfidence: 0.5 });
    return faceMesh;
  }

  // ── Capture + analyse ───────────────────────────────────────────────
  async function captureAndAnalyze() {
    try {
      setStatus('Scanning your skin…');
      const w = videoEl.videoWidth || 1280;
      const h = videoEl.videoHeight || 720;
      captureCanvas.width = w; captureCanvas.height = h;
      const cctx = captureCanvas.getContext('2d');
      cctx.drawImage(videoEl, 0, 0, w, h);
      lastFrameW = w; lastFrameH = h;
      lastFrame = cctx.getImageData(0, 0, w, h);

      await ensureFaceMesh();
      const results = await new Promise((resolve) => {
        faceMesh.onResults(resolve);
        faceMesh.send({ image: captureCanvas });
      });

      if (!results.multiFaceLandmarks || !results.multiFaceLandmarks.length) {
        setStatus('No face detected. Move closer with good front lighting and try again.', true);
        return;
      }
      lastLandmarks = results.multiFaceLandmarks[0];

      // Stop the live camera the moment we have the frame + landmarks so the
      // device LED turns off during the perceived analysis phase.
      stopCamera();

      // Play the 4-second scanning animation on the captured frame so the user
      // perceives an actual analysis (rather than just a quick photo filter).
      await runScanAnimation(captureCanvas, 4000);

      const metrics = analyzeMetrics(lastFrame, lastLandmarks, w, h);
      const recommendations = recommendProducts(metrics);
      const tips = generateTips(metrics);
      const foods = generateFoodAdvice(metrics);

      // Compose before / after images
      const beforeDataUrl = captureCanvas.toDataURL('image/jpeg', 0.85);
      const afterDataUrl  = renderAfter(captureCanvas, lastLandmarks, metrics, recommendations);

      lastReport = { metrics, recommendations, tips, foods, beforeDataUrl, afterDataUrl, timestamp: new Date().toISOString() };
      renderReport(lastReport);
      showPanel('report');
    } catch (e) {
      console.error('[JEJU AI] analyze error', e);
      setStatus('Analysis failed: ' + (e.message || e), true);
    }
  }

  // ── 4-second scanning animation ─────────────────────────────────────
  // Switches to the 'scanning' panel, paints the captured frame onto its
  // canvas, lights up the six metric checklist items one-by-one, then
  // resolves. The sweep + grid CSS animations handle the visual effect.
  function runScanAnimation(srcCanvas, totalMs) {
    return new Promise((resolve) => {
      showPanel('scanning');
      const scanCanvas = dialog.querySelector('[data-jeju-ai-scan-canvas]');
      const checks = Array.from(dialog.querySelectorAll('[data-jeju-ai-scan-checks] li') || []);
      const status = dialog.querySelector('[data-jeju-ai-scan-status]');
      // Paint captured frame into the visible scan canvas
      if (scanCanvas) {
        scanCanvas.width = srcCanvas.width;
        scanCanvas.height = srcCanvas.height;
        scanCanvas.getContext('2d').drawImage(srcCanvas, 0, 0);
      }
      // Reset checklist
      checks.forEach(li => li.classList.remove('is-active', 'is-done'));
      const stepMs = Math.max(300, Math.floor(totalMs / (checks.length + 1)));
      let i = 0;
      const tick = () => {
        if (i > 0 && checks[i - 1]) {
          checks[i - 1].classList.remove('is-active');
          checks[i - 1].classList.add('is-done');
        }
        if (i < checks.length) {
          checks[i].classList.add('is-active');
          if (status) status.textContent = checks[i].textContent.replace(/…$/, '…');
          i++;
          setTimeout(tick, stepMs);
        } else {
          if (status) status.textContent = 'Building your personalised report…';
          setTimeout(resolve, stepMs);
        }
      };
      tick();
    });
  }

  // ── Helpers: landmarks & polygons ───────────────────────────────────
  const LM = {
    FACE_OVAL: [10,338,297,332,284,251,389,356,454,323,361,288,397,365,379,378,400,377,152,148,176,149,150,136,172,58,132,93,234,127,162,21,54,103,67,109],
    LEFT_CHEEK:  [50,101,118,117,123,147,213,192,207,205,36,142],
    RIGHT_CHEEK: [280,330,347,346,352,376,433,416,427,425,266,371],
    FOREHEAD:    [10,338,297,332,284,251,389,356,454,323,361,288,67,109,10,151,108,69,104,68,71,139],
    NOSE_BRIDGE: [168,6,197,195,5,4,1,19,94],
    LEFT_UNDEREYE_LOWER:  [33,130,25,110,24,23,22,26,112,243],
    RIGHT_UNDEREYE_LOWER: [263,359,255,339,254,253,252,256,341,463],
    // Feature regions to EXCLUDE from skin smoothing/red-removal
    LEFT_EYE:   [33,246,161,160,159,158,157,173,133,155,154,153,145,144,163,7],
    RIGHT_EYE:  [263,466,388,387,386,385,384,398,362,382,381,380,374,373,390,249],
    LEFT_BROW:  [70,63,105,66,107,55,65,52,53,46],
    RIGHT_BROW: [300,293,334,296,336,285,295,282,283,276],
    LIPS_OUTER: [61,146,91,181,84,17,314,405,321,375,291,409,270,269,267,0,37,39,40,185],
    NOSTRILS:   [219,235,75,98,2,326,327,305,455,439],
    // Jaw landmarks for the face-slim warp
    JAW_LEFT:  [234,93,132,58,172,136,150,149,176,148,152],
    JAW_RIGHT: [454,323,361,288,397,365,379,378,400,377,152]
  };
  function pt(lm, i, w, h) { const p = lm[i]; return [p.x * w, p.y * h]; }
  function pathPoints(lm, idxs, w, h) { return idxs.map(i => pt(lm, i, w, h)); }
  function bbox(points) {
    let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
    for (const [x, y] of points) { if (x<minX) minX=x; if (y<minY) minY=y; if (x>maxX) maxX=x; if (y>maxY) maxY=y; }
    return { minX: Math.max(0,minX|0), minY: Math.max(0,minY|0), maxX: maxX|0, maxY: maxY|0 };
  }
  function pointInPoly(x, y, poly) {
    let inside = false;
    for (let i=0, j=poly.length-1; i<poly.length; j=i++) {
      const [xi, yi] = poly[i], [xj, yj] = poly[j];
      const intersect = ((yi>y)!==(yj>y)) && (x < (xj-xi)*(y-yi)/((yj-yi)||1e-9)+xi);
      if (intersect) inside = !inside;
    }
    return inside;
  }

  // ── Skin-region pixel sampler ───────────────────────────────────────
  function samplePoly(imageData, poly, w) {
    const bb = bbox(poly);
    const data = imageData.data;
    const samples = [];
    // Downsample for performance — step depends on region size
    const span = Math.max(bb.maxX-bb.minX, bb.maxY-bb.minY) || 1;
    const step = Math.max(2, Math.round(span / 80));
    for (let y = bb.minY; y < bb.maxY; y += step) {
      for (let x = bb.minX; x < bb.maxX; x += step) {
        if (!pointInPoly(x+0.5, y+0.5, poly)) continue;
        const i = (y*w + x) * 4;
        samples.push([data[i], data[i+1], data[i+2]]);
      }
    }
    return samples;
  }
  function rgbStats(samples) {
    if (!samples.length) return null;
    let sR=0,sG=0,sB=0,sL=0;
    const Ls = [];
    for (const [r,g,b] of samples) {
      sR+=r; sG+=g; sB+=b;
      const L = 0.299*r + 0.587*g + 0.114*b;
      sL+=L; Ls.push(L);
    }
    const n = samples.length;
    const meanL = sL/n;
    let varL = 0;
    for (const L of Ls) varL += (L-meanL)*(L-meanL);
    varL /= n;
    return {
      n,
      meanR: sR/n, meanG: sG/n, meanB: sB/n,
      meanL,
      stdL: Math.sqrt(varL),
      redness: (sR - (sG+sB)*0.5) / n, // positive = reddish
      // Count "dark" pixels (≥20 below mean) and "red" pixels
      darkRatio: Ls.filter(L => L < meanL - 18).length / n,
      redRatio:  samples.filter(([r,g,b]) => r - (g+b)*0.5 > 18).length / n
    };
  }

  // ── Analyzer: 6 metrics, each scored 0-100 ──────────────────────────
  function analyzeMetrics(imageData, lm, w, h) {
    const cheekL = samplePoly(imageData, pathPoints(lm, LM.LEFT_CHEEK,  w, h), w);
    const cheekR = samplePoly(imageData, pathPoints(lm, LM.RIGHT_CHEEK, w, h), w);
    const fore   = samplePoly(imageData, pathPoints(lm, LM.FOREHEAD,    w, h), w);
    const nose   = samplePoly(imageData, pathPoints(lm, LM.NOSE_BRIDGE, w, h), w);
    const eyeL   = samplePoly(imageData, pathPoints(lm, LM.LEFT_UNDEREYE_LOWER,  w, h), w);
    const eyeR   = samplePoly(imageData, pathPoints(lm, LM.RIGHT_UNDEREYE_LOWER, w, h), w);

    const cheek = rgbStats([...cheekL, ...cheekR]) || { meanL: 130, stdL: 8, darkRatio: 0, redRatio: 0, redness: 0 };
    const foreS = rgbStats(fore)  || cheek;
    const noseS = rgbStats(nose)  || cheek;
    const eyeS  = rgbStats([...eyeL, ...eyeR]) || cheek;

    // ── Dark spots — % of cheek+forehead pixels markedly darker than mean
    const spotScore = clip(((cheek.darkRatio + foreS.darkRatio) * 0.5) * 400, 5, 100);
    // ── Redness — combined red-ratio + global red dominance
    const redScore  = clip(((cheek.redRatio + foreS.redRatio) * 0.5) * 320 + Math.max(0, cheek.redness) * 1.4, 5, 100);
    // ── Under-eye darkness — how much darker than cheek
    const eyeDelta  = cheek.meanL - eyeS.meanL;
    const eyeScore  = clip(eyeDelta * 4.5, 5, 100);
    // ── Texture irregularity — stdev of cheek luminance (higher = more uneven)
    const textureScore = clip((cheek.stdL - 6) * 9, 5, 100);
    // ── Oiliness — nose-bridge highlights (mean L well above cheek)
    const oilScore  = clip((noseS.meanL - cheek.meanL) * 5 + (noseS.stdL - cheek.stdL) * 3, 5, 100);
    // ── Dryness — low stdev + low specular on nose (dull / flat surface)
    const dryScore  = clip(40 - (noseS.stdL - cheek.stdL) * 4 - Math.max(0, noseS.meanL - cheek.meanL) * 2, 5, 100);

    return {
      darkSpots:   Math.round(spotScore),
      redness:     Math.round(redScore),
      underEye:    Math.round(eyeScore),
      texture:     Math.round(textureScore),
      oiliness:    Math.round(oilScore),
      dryness:     Math.round(dryScore)
    };
  }
  function clip(v, lo, hi) { return Math.max(lo, Math.min(hi, v)); }

  // ── Recommendation engine ───────────────────────────────────────────
  function recommendProducts(m) {
    const recs = [];
    const add = (key, reason, priority) => {
      if (recs.find(r => r.handle === CATALOG[key].handle)) return;
      recs.push({ ...CATALOG[key], reason, priority });
    };

    // Always: SPF in the morning — non-negotiable dermatologist standard
    add('sunscreen', 'Daily UV is the #1 cause of new dark spots and premature aging.', 1);

    if (m.darkSpots >= 35) {
      add('spot', `Dark-spot score ${m.darkSpots}/100 — targeted brightening is your priority.`,
          m.darkSpots >= 60 ? 2 : 3);
    }
    if (m.underEye >= 30) {
      add('eye', `Under-eye darkness score ${m.underEye}/100 — needs hydration & micro-circulation.`,
          m.underEye >= 55 ? 2 : 3);
    }
    if (m.texture >= 40 || m.darkSpots >= 50) {
      add('regenerate', `Texture/scarring score ${m.texture}/100 — overnight regeneration repairs the barrier.`, 3);
    }
    if (m.redness >= 45 || m.texture >= 60) {
      add('ageReversal', `Inflammation/texture indicate barrier weakening — peptides & ceramides restore firmness.`, 3);
    }
    // If multiple high concerns → the Set is the most cost-effective routine
    const highCount = [m.darkSpots,m.redness,m.underEye,m.texture].filter(s => s >= 45).length;
    if (highCount >= 3) {
      add('set', 'Multiple concerns benefit most from the complete 4-step JEJU routine (cleanse → treat → repair → protect).', 1);
    }
    // Lip care always recommended as gentle upsell
    add('lip', 'Lips have no oil glands — daily hydration prevents fine vertical lines.', 5);
    // IPL device for moderate-to-severe combo concerns
    if (m.darkSpots >= 55 && m.texture >= 45) {
      add('ipl', 'IPL accelerates spot fading & collagen synthesis between skincare sessions.', 4);
    }

    recs.sort((a, b) => a.priority - b.priority);
    return recs;
  }

  // ── Dermatologist tips (personalised) ───────────────────────────────
  function generateTips(m) {
    const tips = [
      'Splash face with cold water (or ice-water for 30 sec) before applying JEJU serums — cold tightens pores and boosts product absorption by up to 30%.',
      'Apply skincare from THINNEST to THICKEST: cleanser → toner → serum → eye cream → moisturiser → sunscreen.',
      'Pat — never rub. Rubbing breaks down collagen and pulls down the under-eye area.',
      'Wait 60 seconds between layers so each product can absorb properly.'
    ];
    if (m.darkSpots >= 40) tips.push('Never skip SPF 50 — a single sunburn can undo 30 days of spot-fading.');
    if (m.underEye >= 40) tips.push('Sleep on your back with a silk pillowcase; side-sleeping creates fluid pooling under the eyes.');
    if (m.oiliness >= 50)  tips.push('Double-cleanse at night (oil cleanser first, then water-based). Skipping this is the #1 cause of clogged pores.');
    if (m.dryness  >= 50)  tips.push('Apply moisturiser onto DAMP skin within 60 seconds of cleansing to trap 3× more hydration.');
    if (m.redness  >= 45)  tips.push('Avoid hot showers above 38°C on your face — heat triggers histamine release and capillary flushing.');
    if (m.texture  >= 50)  tips.push('Exfoliate with a chemical AHA/BHA max 2× per week — physical scrubs cause micro-tears that worsen texture.');
    tips.push('Drink 2.5 L water daily, sleep 7-8 h, and wash your pillowcase every 3 days. Skincare is 30% — lifestyle is 70%.');
    return tips;
  }

  // ── Food advice (personalised) ──────────────────────────────────────
  function generateFoodAdvice(m) {
    const eat = new Set([
      'Wild salmon, sardines, mackerel (omega-3 → reduces inflammation and redness)',
      'Avocado, walnuts, flaxseed (healthy fats → supports skin barrier)',
      'Berries, pomegranate, dark leafy greens (antioxidants → fight free-radical aging)',
      'Green tea & matcha (polyphenols → calm skin)',
      'Sweet potato, carrots, mango (beta-carotene → natural glow)',
      'Bone broth, collagen peptides (amino acids → firmness)'
    ]);
    const avoid = new Set([
      'High-glycaemic sugars and white bread (spike insulin → triggers acne & glycation wrinkles)',
      'Excessive dairy, especially skim milk (hormonal acne trigger)',
      'Deep-fried and trans-fat foods (oxidative damage)',
      'More than 2 cups of coffee or 1 alcoholic drink/day (dehydration → dullness)'
    ]);
    if (m.redness  >= 45) eat.add('Cucumber, watermelon, celery (cooling + hydrating)');
    if (m.darkSpots>= 40) eat.add('Citrus, kiwi, bell pepper (vitamin C → brightens pigment)');
    if (m.underEye >= 40) eat.add('Iron-rich beets, lentils, spinach (oxygenates blood vessels around eyes)');
    if (m.oiliness >= 50) avoid.add('High-dairy + high-sugar combo desserts (worst combo for sebum production)');
    if (m.dryness  >= 50) eat.add('Coconut water and chia-seed pudding (cellular hydration)');
    return { eat: [...eat], avoid: [...avoid] };
  }

  // ── 30-day "after" preview ──────────────────────────────────────────
  // BeautyPlus-style pipeline:
  //   1) Build a precise face mask (oval + forehead − eyes/brows/mouth/nostrils, feathered).
  //   2) Per-pixel red-blemish reduction (R-dominance flatten toward local skin tone).
  //   3) Per-pixel tone evening (pull toward median skin colour).
  //   4) Frequency-separation skin smoothing (low-freq blur + high-freq detail kept).
  //   5) Subtle geometric face-slim (compress jaw horizontally).
  //   6) Mask-accurate brighten + warm glow + under-eye lift.
  function renderAfter(srcCanvas, lm, metrics, recs) {
    const w = srcCanvas.width, h = srcCanvas.height;
    const out = document.createElement('canvas');
    out.width = w; out.height = h;
    const ctx = out.getContext('2d');
    ctx.drawImage(srcCanvas, 0, 0);

    const profile = {
      spot_fade:      metrics.darkSpots / 100,
      redness_reduce: metrics.redness   / 100,
      undereye_lift:  metrics.underEye  / 100,
      smooth:         Math.max(0.45, metrics.texture / 100),
      brighten:       0.55,
      even_tone:      Math.min(1, (metrics.darkSpots + metrics.redness) / 150),
      hydrate:        metrics.dryness   / 100,
      clarify:        Math.min(1, (metrics.oiliness + metrics.redness) / 160),
      rejuvenate:     Math.min(1, (metrics.texture + metrics.darkSpots) / 150),
      warm_glow:      0.35,
      firm:           0.55,
      sun_glow:       0.2
    };
    applyAfterEffects(ctx, lm, w, h, profile);
    drawJejuWatermark(ctx, w, h);
    return out.toDataURL('image/jpeg', 0.9);
  }

  // ── JEJU logo watermark (top-centre of the after image) ─────────────
  function drawJejuWatermark(ctx, w, h) {
    const padTop = Math.max(14, h * 0.035);
    const pillH  = Math.max(28, h * 0.065);
    const fontPx = Math.max(14, h * 0.034);
    const subPx  = Math.max(10, h * 0.022);
    const text    = 'JEJU';
    const subText = 'AI SKINCARE';

    ctx.save();
    ctx.font = `800 ${fontPx}px -apple-system, "Segoe UI", Roboto, Helvetica, Arial, sans-serif`;
    const tW = ctx.measureText(text).width;
    ctx.font = `600 ${subPx}px -apple-system, "Segoe UI", Roboto, Helvetica, Arial, sans-serif`;
    const sW = ctx.measureText(subText).width;
    const padX = pillH * 0.55;
    const gap  = pillH * 0.25;
    const pillW = tW + sW + gap + padX * 2;
    const x = (w - pillW) / 2;
    const y = padTop;
    const r = pillH / 2;

    // Pill background (JEJU navy → cyan gradient, semi-transparent)
    const grad = ctx.createLinearGradient(x, y, x + pillW, y);
    grad.addColorStop(0,   'rgba(10, 36, 99, 0.82)');
    grad.addColorStop(0.6, 'rgba(29, 78, 216, 0.82)');
    grad.addColorStop(1,   'rgba(6, 182, 212, 0.82)');
    ctx.beginPath();
    ctx.moveTo(x + r, y);
    ctx.arcTo(x + pillW, y, x + pillW, y + pillH, r);
    ctx.arcTo(x + pillW, y + pillH, x, y + pillH, r);
    ctx.arcTo(x, y + pillH, x, y, r);
    ctx.arcTo(x, y, x + pillW, y, r);
    ctx.closePath();
    ctx.fillStyle = grad;
    ctx.shadowColor = 'rgba(0,0,0,0.35)';
    ctx.shadowBlur = pillH * 0.35;
    ctx.shadowOffsetY = pillH * 0.10;
    ctx.fill();
    ctx.shadowColor = 'transparent';

    // Text — baseline centred vertically in the pill
    ctx.textBaseline = 'middle';
    ctx.fillStyle = '#ffffff';
    ctx.font = `800 ${fontPx}px -apple-system, "Segoe UI", Roboto, Helvetica, Arial, sans-serif`;
    ctx.fillText(text, x + padX, y + pillH / 2);
    ctx.font = `600 ${subPx}px -apple-system, "Segoe UI", Roboto, Helvetica, Arial, sans-serif`;
    ctx.fillStyle = 'rgba(186, 230, 253, 0.95)';
    ctx.fillText(subText, x + padX + tW + gap, y + pillH / 2 + (fontPx - subPx) * 0.15);

    ctx.restore();
  }

  // ── Mask builder ────────────────────────────────────────────────────
  // Returns { canvas, alpha } — alpha is a Uint8ClampedArray of length w*h
  // (one byte per pixel) for fast per-pixel gating.
  function buildFaceMask(lm, w, h) {
    const m = document.createElement('canvas'); m.width = w; m.height = h;
    const c = m.getContext('2d');

    const tracePoly = (idxs) => {
      c.beginPath();
      idxs.forEach((i, n) => {
        const p = lm[i]; const x = p.x*w, y = p.y*h;
        n ? c.lineTo(x, y) : c.moveTo(x, y);
      });
      c.closePath();
    };

    // 1) Fill the face oval (this already includes the forehead via the
    //    top indices 10/109/67/297/338). Earlier we extended further with
    //    an additional ellipse, but that produced a visible shade line on
    //    the forehead where the half-ellipse joined the oval — removed.
    c.fillStyle = '#fff';
    tracePoly(LM.FACE_OVAL); c.fill();

    // 2) Subtract features (eyes, brows, mouth, nostrils) so we don't smear them.
    c.globalCompositeOperation = 'destination-out';
    [LM.LEFT_EYE, LM.RIGHT_EYE, LM.LEFT_BROW, LM.RIGHT_BROW, LM.LIPS_OUTER, LM.NOSTRILS]
      .forEach(idxs => { tracePoly(idxs); c.fill(); });
    c.globalCompositeOperation = 'source-over';

    // 3) Feather the mask edges so blending is invisible
    const feathered = document.createElement('canvas');
    feathered.width = w; feathered.height = h;
    const fc = feathered.getContext('2d');
    fc.filter = `blur(${Math.max(6, Math.min(w, h) * 0.018)}px)`;
    fc.drawImage(m, 0, 0);
    fc.filter = 'none';

    // 4) Extract alpha for per-pixel gate
    const id = fc.getImageData(0, 0, w, h);
    const alpha = new Uint8ClampedArray(w * h);
    for (let i = 0, j = 3; i < alpha.length; i++, j += 4) alpha[i] = id.data[j];

    return { canvas: feathered, alpha };
  }

  // ── YCbCr skin gate ─────────────────────────────────────────────────
  function isSkin(r, g, b) {
    const y  =  0.299*r + 0.587*g + 0.114*b;
    if (y < 40 || y > 240) return false;
    const cb = -0.169*r - 0.331*g + 0.500*b + 128;
    const cr =  0.500*r - 0.419*g - 0.081*b + 128;
    return cb >= 77 && cb <= 130 && cr >= 130 && cr <= 178;
  }

  // ── Median skin colour inside the face mask (for tone evening) ──────
  function meanSkinColour(data, alpha, w, h) {
    let sR = 0, sG = 0, sB = 0, n = 0;
    const step = Math.max(2, Math.round(Math.min(w, h) / 220));
    for (let y = 0; y < h; y += step) {
      const row = y * w;
      for (let x = 0; x < w; x += step) {
        if (alpha[row + x] < 160) continue;
        const i = (row + x) * 4;
        const r = data[i], g = data[i+1], b = data[i+2];
        if (!isSkin(r, g, b)) continue;
        sR += r; sG += g; sB += b; n++;
      }
    }
    if (!n) return null;
    return { r: sR / n, g: sG / n, b: sB / n };
  }

  // ── Per-pixel red-blemish reduction + tone evening ──────────────────
  function correctSkinPixels(ctx, alpha, w, h, redStrength, toneStrength) {
    const img = ctx.getImageData(0, 0, w, h);
    const d = img.data;
    const mean = meanSkinColour(d, alpha, w, h);
    if (!mean) { ctx.putImageData(img, 0, 0); return; }

    const rW = Math.min(0.85, redStrength);
    const tW = Math.min(0.55, toneStrength);

    for (let i = 0, p = 0; p < alpha.length; p++, i += 4) {
      const a = alpha[p];
      if (a < 40) continue;
      const r = d[i], g = d[i+1], b = d[i+2];
      if (!isSkin(r, g, b)) continue;

      const aw = a / 255;

      // Red-dominance correction: how much redder than the mean of G/B
      const rDom = r - (g + b) * 0.5;
      if (rDom > 14 && rW > 0) {
        // Strength scales with how red the pixel is (max around rDom=60)
        const s = Math.min(1, (rDom - 14) / 46) * rW * aw;
        d[i]   = r + (mean.r - r) * s;
        d[i+1] = g + (mean.g - g) * s * 0.6;
        d[i+2] = b + (mean.b - b) * s * 0.6;
      }

      // Tone evening: gentle pull toward median skin colour
      if (tW > 0) {
        const ts = tW * aw * 0.5;
        d[i]   = d[i]   + (mean.r - d[i])   * ts;
        d[i+1] = d[i+1] + (mean.g - d[i+1]) * ts;
        d[i+2] = d[i+2] + (mean.b - d[i+2]) * ts;
      }
    }

    ctx.putImageData(img, 0, 0);
  }

  // ── Frequency-separation skin smoothing ─────────────────────────────
  // low = heavy blur (no pores)  ;  detail = original − low (pores/edges)
  // result = low + detail * keep   →  poreless tone, edges preserved.
  function freqSepSmooth(ctx, maskCanvas, w, h, strength) {
    const r = Math.max(3, Math.min(w, h) * 0.012) * (0.6 + strength * 0.6); // gentle blur radius
    // keepDetail: how much of the ORIGINAL pores/edges to retain.
    // Floor at 0.78 so skin always keeps real texture (no porcelain look).
    const keepDetail = Math.max(0.78, 1 - strength * 0.22);

    // 1) Low-frequency layer (blurred original)
    const low = document.createElement('canvas'); low.width = w; low.height = h;
    const lctx = low.getContext('2d');
    lctx.filter = `blur(${r.toFixed(1)}px)`;
    lctx.drawImage(ctx.canvas, 0, 0);
    lctx.filter = 'none';

    // 2) High-frequency detail layer = original − low (encoded around mid-grey)
    //    detail = original*1 + low*(-1) + 128 → implemented via 'difference' + invert trick
    //    Simpler approach: pre-render `low * (1 - keepDetail)` and additively blend it
    //    onto the original. That's equivalent to:
    //        out = original * keepDetail + low * (1 - keepDetail)
    //    which is exactly a per-pixel linear interpolation toward the blur.
    const blendAlpha = 1 - keepDetail;

    // 3) Mask the low layer to the face only (so background, hair, eyes stay sharp)
    lctx.globalCompositeOperation = 'destination-in';
    lctx.drawImage(maskCanvas, 0, 0);
    lctx.globalCompositeOperation = 'source-over';

    // 4) Composite onto target
    ctx.save();
    ctx.globalAlpha = blendAlpha;
    ctx.drawImage(low, 0, 0);
    ctx.restore();
  }

  // ── Subtle geometric face-slim (BeautyPlus "Reshape") ───────────────
  // Compresses the face horizontally by `pct` (e.g. 0.03 = 3% slimmer),
  // re-pasting the slimmed face back onto the original through the mask.
  function slimFace(ctx, mask, w, h, pct) {
    if (pct <= 0.002) return;

    // 1) Render the masked face only onto a temp canvas
    const face = document.createElement('canvas'); face.width = w; face.height = h;
    const fctx = face.getContext('2d');
    fctx.drawImage(ctx.canvas, 0, 0);
    fctx.globalCompositeOperation = 'destination-in';
    fctx.drawImage(mask.canvas, 0, 0);
    fctx.globalCompositeOperation = 'source-over';

    // 2) Compute the face bounding box from the mask alpha
    let minX = w, minY = h, maxX = 0, maxY = 0;
    const step = 4;
    for (let y = 0; y < h; y += step) {
      const row = y * w;
      for (let x = 0; x < w; x += step) {
        if (mask.alpha[row + x] > 60) {
          if (x < minX) minX = x; if (x > maxX) maxX = x;
          if (y < minY) minY = y; if (y > maxY) maxY = y;
        }
      }
    }
    if (maxX <= minX) return;
    const cx = (minX + maxX) / 2;
    const scaleX = 1 - pct;

    // 3) Erase the original face area (only the masked region) so the slimmed
    //    one paints cleanly without duplication.
    ctx.save();
    ctx.globalCompositeOperation = 'destination-out';
    ctx.drawImage(mask.canvas, 0, 0);
    ctx.restore();

    // 4) Paint the slimmed face back, scaled around the face centre X
    ctx.save();
    ctx.translate(cx, 0);
    ctx.scale(scaleX, 1);
    ctx.translate(-cx, 0);
    ctx.drawImage(face, 0, 0);
    ctx.restore();
  }

  function applyAfterEffects(ctx, lm, w, h, P) {
    const k = 0.45; // realism cap — back to the previous natural look
    const I = v => Math.min(1, (v || 0) * k);

    // Build precise face mask once (oval + forehead − eyes/brows/mouth/nostrils, feathered)
    const mask = buildFaceMask(lm, w, h);

    // ── Pass 1: per-pixel red-blemish + dark-spot removal + tone evening
    //   spot_fade gets a heavier weight here so dark spots fade more.
    const redStr  = Math.min(0.65, I(P.redness_reduce) * 0.95 + I(P.spot_fade) * 0.55);
    const toneStr = Math.min(0.30, I(P.even_tone) * 0.50);
    if (redStr > 0.04 || toneStr > 0.04) {
      correctSkinPixels(ctx, mask.alpha, w, h, redStr, toneStr);
    }

    // ── Pass 2: spot-fade highlight pass — fills dark spots with local-average tone
    if (I(P.spot_fade) > 0.04) {
      const s = I(P.spot_fade);
      const buf = document.createElement('canvas'); buf.width = w; buf.height = h;
      const bctx = buf.getContext('2d');
      bctx.filter = `blur(${(8 + s * 12).toFixed(1)}px)`;
      bctx.drawImage(ctx.canvas, 0, 0);
      bctx.filter = 'none';
      bctx.globalCompositeOperation = 'destination-in';
      bctx.drawImage(mask.canvas, 0, 0);
      bctx.globalCompositeOperation = 'source-over';

      ctx.save();
      ctx.globalCompositeOperation = 'lighten';
      ctx.globalAlpha = Math.min(0.42, s * 0.55);
      ctx.drawImage(buf, 0, 0);
      ctx.restore();
    }

    // ── Pass 3: frequency-separation skin smoothing (capped low so pores stay)
    const smoothStr = Math.min(0.55, I(P.smooth) + I(P.clarify) * 0.25 + I(P.hydrate) * 0.15);
    if (smoothStr > 0.05) {
      freqSepSmooth(ctx, mask.canvas, w, h, smoothStr);
    }

    // ── Pass 4: very light brighten + warm glow (masked to face)
    const bright = I(P.brighten), evenT = I(P.even_tone), warm = I(P.warm_glow);
    if (bright > 0.01 || evenT > 0.01 || warm > 0.01) {
      const tint = document.createElement('canvas'); tint.width = w; tint.height = h;
      const tctx = tint.getContext('2d');
      if (bright > 0.01) {
        tctx.fillStyle = `rgba(255,248,235,${Math.min(0.18, bright * 0.25)})`;
        tctx.fillRect(0, 0, w, h);
      }
      if (warm > 0.01) {
        tctx.fillStyle = `rgba(232,196,148,${Math.min(0.10, warm * 0.16)})`;
        tctx.fillRect(0, 0, w, h);
      }
      tctx.globalCompositeOperation = 'destination-in';
      tctx.drawImage(mask.canvas, 0, 0);

      ctx.save();
      ctx.globalCompositeOperation = 'soft-light';
      ctx.globalAlpha = 0.7;
      ctx.drawImage(tint, 0, 0);
      ctx.restore();
    }

    // ── Pass 5: under-eye tightening + dark-circle fade (younger look)
    //   Vertical squash flattens the eye-bag, then a fade lifts the shadow.
    //   Always fires at minimum 0.25 strength so even low under-eye scores get it.
    const u = Math.max(I(P.undereye_lift), 0.25);
    tightenUnderEye(ctx, lm, w, h, LM.LEFT_UNDEREYE_LOWER,  u);
    tightenUnderEye(ctx, lm, w, h, LM.RIGHT_UNDEREYE_LOWER, u);

    // Face slim disabled — geometry stays untouched (kept the cartoon away).
  }

  // Tighten an under-eye bag region: clip to a slightly-expanded eye-bag
  // polygon, vertically compress the strip by ~6% toward the upper edge,
  // then blend a soft fade + faint highlight on top to lift the shadow.
  function tightenUnderEye(ctx, lm, w, h, idxs, u) {
    // 1. Bounding box of the under-eye polygon
    const pts = idxs.map(i => [lm[i].x * w, lm[i].y * h]);
    let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
    for (const [x, y] of pts) {
      if (x < minX) minX = x; if (x > maxX) maxX = x;
      if (y < minY) minY = y; if (y > maxY) maxY = y;
    }
    const padX = (maxX - minX) * 0.15;
    const padY = (maxY - minY) * 0.30;
    const bx = Math.max(0, minX - padX);
    const by = Math.max(0, minY - padY * 0.4);
    const bw = Math.min(w - bx, (maxX - minX) + padX * 2);
    const bh = Math.min(h - by, (maxY - minY) + padY);

    // 2. Snapshot the strip
    const strip = document.createElement('canvas');
    strip.width = Math.ceil(bw); strip.height = Math.ceil(bh);
    const sctx = strip.getContext('2d');
    sctx.drawImage(ctx.canvas, bx, by, bw, bh, 0, 0, bw, bh);

    // 3. Vertical squash factor (top stays, bottom pulls up = bag flattens)
    const squash = 1 - Math.min(0.08, u * 0.10);

    // 4. Clip-mask: feathered version of the under-eye polygon
    ctx.save();
    const clip = document.createElement('canvas');
    clip.width = w; clip.height = h;
    const cctx = clip.getContext('2d');
    cctx.filter = `blur(${Math.max(3, bh * 0.18).toFixed(1)}px)`;
    cctx.fillStyle = '#fff';
    cctx.beginPath();
    idxs.forEach((idx, n) => {
      const p = lm[idx]; const x = p.x * w, y = p.y * h;
      n ? cctx.lineTo(x, y) : cctx.moveTo(x, y);
    });
    cctx.closePath(); cctx.fill();
    cctx.filter = 'none';

    // 5. Erase the bag area (only inside the mask) so the squashed strip paints clean
    ctx.globalCompositeOperation = 'destination-out';
    ctx.drawImage(clip, 0, 0);
    ctx.globalCompositeOperation = 'source-over';

    // 6. Paint squashed strip back, then re-mask to the under-eye polygon
    const composed = document.createElement('canvas');
    composed.width = w; composed.height = h;
    const ccx = composed.getContext('2d');
    ccx.drawImage(strip, bx, by, bw, bh * squash);
    // Mirror the bottom edge upward a touch to avoid a hard line
    ccx.drawImage(strip, 0, bh * squash - 2, bw, 2, bx, by + bh * squash - 2, bw, 2);
    ccx.globalCompositeOperation = 'destination-in';
    ccx.drawImage(clip, 0, 0);
    ctx.drawImage(composed, 0, 0);
    ctx.restore();

    // 7. Soft fade + faint warm lift on top
    const buf = document.createElement('canvas'); buf.width = w; buf.height = h;
    const bctx = buf.getContext('2d');
    bctx.filter = `blur(${(3 + u * 4).toFixed(1)}px)`;
    bctx.drawImage(ctx.canvas, 0, 0);
    bctx.filter = 'none';
    bctx.globalCompositeOperation = 'destination-in';
    bctx.drawImage(clip, 0, 0);
    bctx.globalCompositeOperation = 'source-over';

    ctx.save();
    ctx.globalCompositeOperation = 'lighten';
    ctx.globalAlpha = Math.min(0.40, u * 0.50);
    ctx.drawImage(buf, 0, 0);
    ctx.restore();
  }

  // ── Render the report ───────────────────────────────────────────────
  function renderReport(r) {
    const root = dialog.querySelector('[data-jeju-ai-report]');
    if (!root) return;
    const m = r.metrics;
    const concernRow = (label, score, lo, hi) => {
      let level = 'Excellent', cls = 'lvl-good';
      if (score >= 60) { level = 'Needs attention'; cls = 'lvl-high'; }
      else if (score >= 35) { level = 'Mild'; cls = 'lvl-mid'; }
      return `
        <li class="jeju-ai-metric ${cls}">
          <span class="jeju-ai-metric-label">${label}</span>
          <span class="jeju-ai-metric-bar"><span style="width:${score}%"></span></span>
          <span class="jeju-ai-metric-score">${score}/100 · ${level}</span>
        </li>`;
    };

    const recs = r.recommendations.map(p => `
      <article class="jeju-ai-product">
        <h4><a href="/products/${p.handle}" target="_blank" rel="noopener">${p.title}</a></h4>
        <p class="jeju-ai-reason">${p.reason}</p>
        <dl>
          <dt>When</dt><dd>${p.use}</dd>
          <dt>How much</dt><dd>${p.dose}</dd>
        </dl>
      </article>
    `).join('');

    const tipsHtml  = r.tips.map(t => `<li>${t}</li>`).join('');
    const eatHtml   = r.foods.eat.map(t => `<li>✔ ${t}</li>`).join('');
    const avoidHtml = r.foods.avoid.map(t => `<li>✘ ${t}</li>`).join('');

    root.innerHTML = `
      <div class="jeju-ai-prev">
        <figure><img src="${r.beforeDataUrl}" alt="Before"><figcaption>Today</figcaption></figure>
        <figure><img src="${r.afterDataUrl}"  alt="After"><figcaption>After 30 days with JEJU</figcaption></figure>
      </div>
      <section class="jeju-ai-section">
        <h3>Your skin analysis</h3>
        <ul class="jeju-ai-metrics">
          ${concernRow('Dark spots / hyperpigmentation', m.darkSpots)}
          ${concernRow('Redness / inflammation',        m.redness)}
          ${concernRow('Under-eye darkness',            m.underEye)}
          ${concernRow('Skin texture irregularity',     m.texture)}
          ${concernRow('Oiliness (T-zone)',             m.oiliness)}
          ${concernRow('Dryness / dullness',            m.dryness)}
        </ul>
      </section>
      <section class="jeju-ai-section">
        <h3>Your JEJU routine</h3>
        <div class="jeju-ai-products">${recs}</div>
      </section>
      <section class="jeju-ai-section">
        <h3>Dermatologist tips for you</h3>
        <ul class="jeju-ai-tips">${tipsHtml}</ul>
      </section>
      <section class="jeju-ai-section jeju-ai-foods">
        <h3>Eat more of this</h3>
        <ul class="jeju-ai-eat">${eatHtml}</ul>
        <h3>Limit / avoid</h3>
        <ul class="jeju-ai-avoid">${avoidHtml}</ul>
      </section>
    `;
  }

  // ── Compose a single PNG report (for download / email attachment) ──
  async function composeReportImage(r) {
    const W = 1080;
    // Compute height dynamically based on text length
    const wrap = (ctx, text, maxWidth) => {
      const words = text.split(' '); const lines = []; let line = '';
      for (const w of words) {
        const t = line ? line + ' ' + w : w;
        if (ctx.measureText(t).width > maxWidth) { lines.push(line); line = w; } else line = t;
      }
      if (line) lines.push(line); return lines;
    };
    const c = document.createElement('canvas');
    const ctx0 = c.getContext('2d');
    ctx0.font = '20px system-ui, sans-serif';
    const tipLines = r.tips.flatMap(t => wrap(ctx0, '• ' + t, W - 80));
    const eatLines = r.foods.eat.flatMap(t => wrap(ctx0, '✔ ' + t, W - 80));
    const avoidLines = r.foods.avoid.flatMap(t => wrap(ctx0, '✘ ' + t, W - 80));
    const recLines = r.recommendations.flatMap(p => [
      '· ' + p.title,
      ...wrap(ctx0, '   ' + p.reason, W - 100),
      ...wrap(ctx0, '   When: ' + p.use, W - 100),
      ...wrap(ctx0, '   Dose: ' + p.dose, W - 100),
      ''
    ]);

    const HEAD = 120, PREV = 520, GAP = 24;
    const sec  = (lines) => 60 + lines.length * 28 + GAP;
    const total = HEAD + PREV + sec(['','','','','','','']) /* metrics */
                + sec(recLines) + sec(tipLines) + sec(eatLines) + sec(avoidLines) + 80;
    c.width = W; c.height = total;
    const ctx = c.getContext('2d');

    // Background
    ctx.fillStyle = '#fff'; ctx.fillRect(0,0,W,total);
    // Header
    ctx.fillStyle = '#101622';
    ctx.font = 'bold 36px system-ui, sans-serif';
    ctx.fillText('JEJU AI Skincare Diagnosis', 40, 60);
    ctx.font = '18px system-ui, sans-serif';
    ctx.fillStyle = '#666';
    ctx.fillText(new Date(r.timestamp).toLocaleString(), 40, 90);

    let y = HEAD;
    // Before / After
    await new Promise(res => {
      const a = new Image(), b = new Image(); let n = 0;
      const done = () => { if (++n === 2) res(); };
      a.onload = b.onload = done;
      a.onerror = b.onerror = done;
      a.src = r.beforeDataUrl; b.src = r.afterDataUrl;
      setTimeout(res, 2500);
      // After both load draw inside the await
      Promise.all([a.decode().catch(()=>{}), b.decode().catch(()=>{})]).then(() => {
        const halfW = (W - 60) / 2;
        const drawFit = (img, x) => {
          if (!img.width) return;
          const ratio = img.width / img.height;
          let dw = halfW, dh = halfW / ratio;
          if (dh > PREV - 40) { dh = PREV - 40; dw = dh * ratio; }
          const dx = x + (halfW - dw) / 2, dy = y + (PREV - 40 - dh) / 2;
          ctx.drawImage(img, dx, dy, dw, dh);
        };
        drawFit(a, 20);
        drawFit(b, 40 + halfW);
        ctx.font = 'bold 18px system-ui, sans-serif';
        ctx.fillStyle = '#101622';
        ctx.fillText('Today', 20 + halfW/2 - 25, y + PREV - 10);
        ctx.fillText('After 30 days', 40 + halfW + halfW/2 - 60, y + PREV - 10);
        res();
      });
    });
    y += PREV + GAP;

    const writeSection = (title, lines, color) => {
      ctx.fillStyle = color || '#101622';
      ctx.font = 'bold 24px system-ui, sans-serif';
      ctx.fillText(title, 40, y);
      y += 36;
      ctx.font = '18px system-ui, sans-serif';
      ctx.fillStyle = '#222';
      for (const line of lines) { ctx.fillText(line, 40, y); y += 26; }
      y += GAP;
    };

    const m = r.metrics;
    const metricLines = [
      `Dark spots / hyperpigmentation: ${m.darkSpots}/100`,
      `Redness / inflammation:         ${m.redness}/100`,
      `Under-eye darkness:             ${m.underEye}/100`,
      `Skin texture irregularity:      ${m.texture}/100`,
      `Oiliness (T-zone):              ${m.oiliness}/100`,
      `Dryness / dullness:             ${m.dryness}/100`
    ];
    writeSection('Your skin analysis', metricLines);
    writeSection('Your JEJU routine', recLines);
    writeSection('Dermatologist tips for you', tipLines);
    writeSection('Eat more of this', eatLines, '#1e6f3a');
    writeSection('Limit / avoid', avoidLines, '#9b1c1c');

    return c.toDataURL('image/jpeg', 0.85);
  }

  // ── Download report ─────────────────────────────────────────────────
  async function downloadReport() {
    if (!lastReport) return;
    const url = await composeReportImage(lastReport);
    const a = document.createElement('a');
    a.href = url;
    a.download = `JEJU-AI-Skincare-Report-${Date.now()}.jpg`;
    document.body.appendChild(a); a.click(); a.remove();
  }

  // ── Email submission (uses Shopify contact form endpoint) ──────────
  async function submitEmail(ev) {
    ev.preventDefault();
    if (!lastReport) return;
    const form = ev.currentTarget;
    const email = form.querySelector('[name="contact[email]"]').value.trim();
    const name  = form.querySelector('[name="contact[name]"]')?.value.trim() || '';
    if (!email) return;

    const statusEl = form.querySelector('[data-jeju-ai-email-status]');
    statusEl.textContent = 'Preparing your report…';

    const m = lastReport.metrics;
    const body = [
      `JEJU AI Skincare Diagnosis for ${name || email}`,
      `Date: ${new Date(lastReport.timestamp).toLocaleString()}`,
      ``,
      `── Skin analysis ──`,
      `Dark spots: ${m.darkSpots}/100`,
      `Redness: ${m.redness}/100`,
      `Under-eye darkness: ${m.underEye}/100`,
      `Texture: ${m.texture}/100`,
      `Oiliness: ${m.oiliness}/100`,
      `Dryness: ${m.dryness}/100`,
      ``,
      `── Recommended JEJU routine ──`,
      ...lastReport.recommendations.map(p =>
        `• ${p.title}\n  ${p.reason}\n  When: ${p.use}\n  Dose: ${p.dose}\n  Link: https://www.jejullc.com/products/${p.handle}`),
      ``,
      `── Dermatologist tips ──`,
      ...lastReport.tips.map(t => '• ' + t),
      ``,
      `── Eat more ──`,
      ...lastReport.foods.eat.map(t => '✔ ' + t),
      ``,
      `── Limit / avoid ──`,
      ...lastReport.foods.avoid.map(t => '✘ ' + t),
      ``,
      `Order now: https://www.jejullc.com/collections/all`,
      ``,
      `(Before & after images attached as a downloaded JPG from the dialog.)`
    ].join('\n');

    // Submit via Shopify contact form (admin gets notified to follow up)
    try {
      const fd = new FormData();
      fd.append('form_type', 'contact');
      fd.append('utf8', '✓');
      fd.append('contact[name]', name || 'JEJU AI Scan');
      fd.append('contact[email]', email);
      fd.append('contact[subject]', 'JEJU AI Skincare report request');
      fd.append('contact[body]', body);
      await fetch('/contact#contact_form', { method: 'POST', body: fd, credentials: 'same-origin' });
    } catch (e) {
      console.warn('[JEJU AI] contact form post failed', e);
    }

    // Also trigger download so the user has the PDF/PNG report immediately
    try { await downloadReport(); } catch (e) {}

    statusEl.textContent = '✓ Your report is being prepared and a copy has been downloaded. Our team will follow up at ' + email + ' within 24 hours with the full diagnosis and your 30-day projection image.';
    form.querySelector('button[type="submit"]').disabled = true;
  }
})();
